/**
 * The reads behind a project's Overview tab (MB's R246; docs/specs/project-reporting/overview-everywhere.md): the rows
 * Teslyn Field's Overview reads, from Teslyn Cloud with the signed-in staff member's rights. `ProjectPage` already
 * holds the blocks, planned lines and line status (`loadProjectPlan`); this reads the rest. RLS lets every staff member
 * of the tenant read these tables, and no client user any of them (a client's Progress tab never comes here).
 *
 * People are read with the deleted ones and aircraft without, as Teslyn Field reads them. Any failed read fails the
 * whole load, so a missing table never shows as a zero. Only the QC rows may fail alone: `qc: null` says the count is
 * not known, and the tab says so.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  overviewSelect,
  projectOverview,
  type Aircraft,
  type DailyEntry,
  type Flight,
  type OverviewQcRows,
  type OverviewRows,
  type OverviewTable,
  type ProjectOverview,
} from '@teslyn/domain';
import { all, plainPortalError, type PortalProject, type ProjectPlan, type Res } from './api.ts';

export interface CloudOverview {
  readonly overview: ProjectOverview;
  /** The rows the "Export CSV" of the daily columns needs. */
  readonly csv: Pick<OverviewRows, 'entries' | 'aircraft' | 'people' | 'statuses' | 'flights' | 'blocks'>;
}

const projectRows = <T>(
  sb: SupabaseClient,
  table: string,
  columns: string,
  projectId: string,
): Promise<T[]> =>
  all<T>(
    (from, to) =>
      sb
        .from(table)
        .select(columns)
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('id')
        .range(from, to) as unknown as PromiseLike<Res<T[]>>,
  );

const overviewRows = <T>(sb: SupabaseClient, table: OverviewTable, projectId: string): Promise<T[]> =>
  projectRows<T>(sb, table, overviewSelect(table), projectId);

async function loadQc(sb: SupabaseClient, projectId: string): Promise<OverviewQcRows | null> {
  try {
    const [runs, lines, acceptances] = await Promise.all([
      overviewRows<OverviewQcRows['runs'][number]>(sb, 'qc_run', projectId),
      overviewRows<OverviewQcRows['lines'][number]>(sb, 'qc_line_result', projectId),
      overviewRows<OverviewQcRows['acceptances'][number]>(sb, 'qc_acceptance', projectId),
    ]);
    return { runs, lines, acceptances };
  } catch {
    return null;
  }
}

export async function loadOverview(
  sb: SupabaseClient,
  project: Pick<PortalProject, 'id' | 'tenantId'>,
  plan: ProjectPlan,
): Promise<CloudOverview> {
  const [head, flights, entries, aircraft, people, qc] = await Promise.all([
    sb.from('project').select('mr_warn_h').eq('id', project.id).maybeSingle() as unknown as PromiseLike<
      Res<{ mr_warn_h: number | null }>
    >,
    projectRows<Flight>(sb, 'flight', '*', project.id),
    // every column: "Export CSV" holds the fuel and the comments
    projectRows<DailyEntry>(sb, 'daily_entry', '*', project.id),
    all<Aircraft>(
      (from, to) =>
        sb
          .from('aircraft')
          .select('*')
          .eq('tenant_id', project.tenantId)
          .is('deleted_at', null)
          .order('id')
          .range(from, to) as unknown as PromiseLike<Res<Aircraft[]>>,
    ),
    all<{ id: string; name: string }>(
      (from, to) =>
        sb
          .from('person')
          .select(overviewSelect('person'))
          .eq('tenant_id', project.tenantId)
          .order('id')
          .range(from, to) as unknown as PromiseLike<Res<{ id: string; name: string }[]>>,
    ),
    loadQc(sb, project.id),
  ]);
  if (head.error) throw new Error(plainPortalError(head.error));
  const rows: OverviewRows = {
    project: { id: project.id, mr_warn_h: head.data?.mr_warn_h ?? null },
    blocks: plan.blocks,
    plan: plan.plan,
    statuses: plan.statuses,
    flights,
    entries,
    aircraft,
    people,
    qc,
  };
  return {
    overview: projectOverview(rows),
    csv: { entries, aircraft, people, statuses: plan.statuses, flights, blocks: plan.blocks },
  };
}
